import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { db, doc, updateDoc, serverTimestamp } from '../../firebase/config';
import SettingsLayout from './SettingsLayout';
import {
  MapPin, Bell, Database, Download, Trash2, Shield, Check, AlertCircle,
  Navigation,
} from 'lucide-react';

const DataPermissionsPage = () => {
  const { user, userData } = useAuth();
  const { currentLocation, requestLocation } = useLocation();

  const [notifications, setNotifications] = useState(userData?.notifications !== false);
  const [toast, setToast] = useState(null);
  const [locLoading, setLocLoading] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const handleToggleNotifications = async () => {
    const next = !notifications;
    setNotifications(next);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        notifications: next,
        updatedAt: serverTimestamp(),
      });
      showToast(next ? 'Notifications enabled' : 'Notifications disabled');
    } catch (err) {
      console.error('Toggle notifications failed:', err);
      setNotifications(!next);
      showToast('Could not update', 'error');
    }
  };

  const handleEnableLocation = async () => {
    setLocLoading(true);
    try {
      const result = await requestLocation();
      if (result?.lat != null) {
        await updateDoc(doc(db, 'users', user.uid), {
          coordinates: { ...result, label: result.label || null },
          updatedAt: serverTimestamp(),
        });
        showToast('Location saved');
      } else {
        showToast('Could not read location', 'error');
      }
    } catch (err) {
      console.error('Location failed:', err);
      showToast('Location permission denied', 'error');
    } finally {
      setLocLoading(false);
    }
  };

  const handleDownloadData = () => {
    const subject = encodeURIComponent('Data export request');
    const body = encodeURIComponent(
      `Hi Skytone team,\n\nPlease send me a copy of all the data you have stored about my account (${user?.email}).\n\nThank you.`
    );
    window.location.href = `mailto:support@skytone.app?subject=${subject}&body=${body}`;
  };

  const handleDeleteAccount = () => {
    const subject = encodeURIComponent('Account deletion request');
    const body = encodeURIComponent(
      `Hi Skytone team,\n\nPlease delete my Skytone account and all associated data for ${user?.email}.\n\nThank you.`
    );
    window.location.href = `mailto:support@skytone.app?subject=${subject}&body=${body}`;
  };

  return (
    <SettingsLayout
      title="Data & Permissions"
      subtitle="Control what Skytone can access"
    >
      {/* Location */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] shrink-0">
            <MapPin size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">Location</h3>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              Used to show you pros and services near you. We only store the coordinates you share.
            </p>

            {currentLocation?.lat ? (
              <div className="mt-3 flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-600 text-[11px] font-bold rounded-full">
                  <Check size={11} />
                  {currentLocation.label || 'Location set'}
                </span>
                <button
                  onClick={handleEnableLocation}
                  disabled={locLoading}
                  className="text-[11px] font-bold text-[#EFA9B6] hover:underline"
                >
                  Update
                </button>
              </div>
            ) : (
              <button
                onClick={handleEnableLocation}
                disabled={locLoading}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-[#EFA9B6] text-white text-xs font-bold rounded-full hover:bg-[#EFA9B6]/90 transition-colors disabled:opacity-50"
              >
                <Navigation size={12} />
                {locLoading ? 'Getting location…' : 'Enable location'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] shrink-0">
            <Bell size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-bold text-gray-900 text-sm">Push notifications</h3>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                  Booking updates, order status, messages from pros.
                </p>
              </div>
              <button
                onClick={handleToggleNotifications}
                className={`w-12 h-6 rounded-full transition-colors relative flex items-center p-0.5 shrink-0 ${
                  notifications ? 'bg-[#EFA9B6]' : 'bg-gray-300'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                  notifications ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Data */}
      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-50">
          <div className="flex items-center gap-2">
            <Database size={16} className="text-[#EFA9B6]" />
            <h3 className="font-serif font-bold text-gray-900">Your data</h3>
          </div>
        </div>

        <button
          onClick={handleDownloadData}
          className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#FCF8F9] transition-colors border-b border-gray-50"
        >
          <div className="w-10 h-10 rounded-xl bg-[#FCF8F9] flex items-center justify-center text-gray-600 shrink-0">
            <Download size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-sm">Download my data</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Request a copy of everything we have on you
            </p>
          </div>
        </button>

        <button
          onClick={handleDeleteAccount}
          className="w-full p-4 flex items-center gap-3 text-left hover:bg-red-50 transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500 shrink-0">
            <Trash2 size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-red-500 text-sm">Delete my account</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Request permanent deletion of your Skytone account
            </p>
          </div>
        </button>
      </div>

      {/* Safety note */}
      <div className="flex items-start gap-2 p-4 bg-[#F4EEFF] rounded-2xl">
        <Shield size={16} className="text-[#AF8BD4] shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600 leading-relaxed">
          Your data is protected under South African POPIA regulations. We only use it to run Skytone — we never sell it.
        </p>
      </div>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[70]">
          <div className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#010101] text-white'
          }`}>
            {toast.msg}
          </div>
        </div>
      )}
    </SettingsLayout>
  );
};

export default DataPermissionsPage;